/**
 * Asset Counts columns (AST-120).
 *
 * The Mat Locations modal becomes Asset Counts on manually counted projects:
 * one column per MANUAL ASSET (ST124) instead of per subtype. Counts saved
 * before manual assets existed get a column named for their subtype
 * (Laminate Mat), after the manual asset columns. Pure, so the column rules
 * are tested without rendering; the table itself is locationTable, fed these
 * columns and the matching columnOf, so location/move rules are unchanged.
 *
 *   AC-R3  a column per manual asset name
 *   AC-R4  only assets with a non-zero count somewhere (locationTable hides
 *          all-zero columns); natural A->Z by name
 *   AC-R5  subtype columns for unassigned counts, AFTER the asset columns
 *   AC-R6  archived assets with counts keep their column
 *   AC-R9  columns are keyed by id, labelled with the current name -- a
 *          rename relabels, never re-buckets
 */

const natural = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const assetCol = (id) => `a:${id}`;
export const subtypeCol = (id) => `s:${id}`;

/**
 * Column key for one entry. An entry naming a manual asset we don't know
 * (names failed to load, or a record from another customer) counts under its
 * subtype, so no mats drop out of the totals.
 */
export const assetColumnOf = (assetById) => (e) => (
  e.manualAssetId && assetById[e.manualAssetId] ? assetCol(e.manualAssetId) : subtypeCol(e.matTypeId)
);

/**
 * Candidate columns, in display order: manual assets A->Z, then subtypes in
 * catalog order. locationTable drops the all-zero ones (AC-R4), so it's safe
 * to offer every asset and every subtype here.
 */
export function assetColumns(manualAssets, subtypes, assetTypeId) {
  const assets = (manualAssets || [])
    .filter((a) => a?.id && a.name && (!assetTypeId || a.assetTypeId === assetTypeId))
    .sort((x, y) => natural(x.name, y.name))
    .map((a) => ({ id: assetCol(a.id), name: a.name, kind: 'asset', archived: !!a.archived }));
  const subs = (subtypes || []).map((s) => ({ id: subtypeCol(s.id), name: s.name, kind: 'subtype', archived: false }));
  return [...assets, ...subs];
}

/**
 * Subtype columns for counts whose subtype isn't in the catalog (retired, or
 * a catalog that hasn't synced). Without these, locationTable would have no
 * column to put them in and the totals would silently shrink.
 */
export function orphanSubtypeColumns(entries, assetById, subtypes) {
  const known = new Set((subtypes || []).map((s) => s.id));
  const seen = new Set();
  const out = [];
  for (const e of entries || []) {
    if (!e?.matTypeId) continue;
    if (e.manualAssetId && assetById[e.manualAssetId]) continue;
    if (known.has(e.matTypeId) || seen.has(e.matTypeId)) continue;
    seen.add(e.matTypeId);
    out.push({ id: subtypeCol(e.matTypeId), name: 'Unknown type', kind: 'subtype', archived: false });
  }
  return out;
}
